import { useCustomerUsersByID } from '@src/hooks/useCustomer';
import {
  Alert,
  AlertVariants,
  AppwideDrawerContentType,
  Button,
  ButtonVariants,
  Dialog,
  DrawerContent,
  DrawerFooter,
  Flex,
  IndicatorBadge,
  IndicatorDotVariants,
  LoaderTalos,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  Text,
  useAppwideDrawerContext,
  useDisclosure,
  useDynamicCallback,
  useTabs,
} from '@talos/kyoko';

import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import { ApiKeyAccountFiltersTab } from './CustomerApiKeys/ApiKeyAccountFiltersTab';
import { CustomerApiKeysFormTable } from './CustomerApiKeys/CustomerApiKeysFormTable';
import { CustomerUserDetails } from './CustomerUserDetails';
import { CustomerUserPermissions } from './CustomerUserPermissions';
import type { Warning } from './CustomerUserPermissions/types';
import { useCustomerUserDetailsDrawer, type NewUserDrawerProps } from './useCustomerUserDetailsDrawer';

export function useCustomerUserDetailsAppwideDrawer() {
  const { openDrawer, drawer } = useAppwideDrawerContext();
  const closeHandlerRef = useRef(drawer.close);

  return useDynamicCallback((customerUserID?: string) => {
    closeHandlerRef.current = drawer.close;
    openDrawer({
      type: AppwideDrawerContentType.CustomerUserDetails,
      width: 650,
      placement: 'right',
      drawerHeaderDesign: 'flex',
      flexHeaderTitle: () => <CustomerUserDetailsDrawerTitle customerUserID={customerUserID} />,
      onClose: () => closeHandlerRef.current(),
      renderContent: () => (
        <CustomerUserDetailsDrawerContent
          customerUserID={customerUserID}
          closeHandlerRef={closeHandlerRef}
          contentTestId="customer-user-details-drawer"
        />
      ),
    });
  });
}

function CustomerUserDetailsDrawerTitle({ customerUserID }: { customerUserID?: string }) {
  const customerUsersByID = useCustomerUsersByID();
  const customerUser = customerUserID ? customerUsersByID?.get(customerUserID) : undefined;

  return (
    <Flex gap="spacing4" alignItems="center" minWidth={0}>
      <Text color="colorTextImportant">{customerUser?.DisplayName ?? 'New User'}</Text>
      {customerUser?.Roles.map(role => (
        <IndicatorBadge key={role.RoleID}>{role.DisplayName}</IndicatorBadge>
      ))}
    </Flex>
  );
}

interface CustomerUserDetailsDrawerContentProps {
  customerUserID?: string;
  closeHandlerRef: MutableRefObject<() => void>;
  contentTestId?: string;
}

function CustomerUserDetailsDrawerContent({
  customerUserID,
  closeHandlerRef,
  contentTestId,
}: CustomerUserDetailsDrawerContentProps) {
  const { drawer } = useAppwideDrawerContext();
  const warningDialog = useDisclosure();
  const [warning, setWarning] = useState<Warning | null>(null);

  const onClose = useCallback(() => {
    setWarning(null);
  }, []);

  const onConfirm = useDynamicCallback(() => {
    warning?.onOk?.();
    onClose();
  });

  const onWarning = useDynamicCallback((warning: Warning) => {
    setWarning(warning);
    warningDialog.open();
  });

  const drawerProps: NewUserDrawerProps = {
    ...drawer,
    customerUserID,
    onClose: drawer.close,
  };
  const { customerUser, handleClose, handleSave, tabDefs, userSettingsRef, isSaveDisabled, isLoading } =
    useCustomerUserDetailsDrawer({ ...drawerProps, onWarning });
  closeHandlerRef.current = handleClose;

  const tabs = useTabs({
    initialItems: tabDefs,
  });

  if (isLoading) {
    return <LoaderTalos />;
  }

  return (
    <Flex flexDirection="column" h="100%" overflow="hidden" data-testid={contentTestId}>
      <DrawerContent rows="1fr">
        <Dialog
          {...warningDialog}
          usePortal={false}
          onConfirm={onConfirm}
          onCancel={onClose}
          title="Confirmation"
          confirmLabel="OK"
          cancelLabel="Cancel"
          showCancel={!!warning?.onOk}
          width={400}
        >
          <Alert dismissable={false} variant={AlertVariants.Warning}>
            <Text>{warning?.message}</Text>
          </Alert>
        </Dialog>
        <Tabs {...tabs} style={{ overflow: 'hidden' }} size={TabSize.Large}>
          <TabList isBordered flex="0 0 auto">
            {tabDefs.map((tab, index) => (
              <Tab key={index} {...tab} showDot={tab.isDirty} dotVariant={IndicatorDotVariants.Primary} />
            ))}
          </TabList>
          <TabPanels h="100%" w="100%" overflow="auto">
            {tabDefs.map(tab => {
              switch (tab.label) {
                case 'General':
                  return <CustomerUserDetails key={tab.label} ref={userSettingsRef} />;
                case 'Account Restrictions':
                  return <CustomerUserPermissions key={tab.label} onWarning={onWarning} />;
                case 'API Keys':
                  return customerUser && <CustomerApiKeysFormTable key={customerUser.CustomerUserID} />;
                case 'API Key Accounts':
                  return customerUser && <ApiKeyAccountFiltersTab key={`filters-${customerUser.CustomerUserID}`} />;
                default:
                  return null;
              }
            })}
          </TabPanels>
        </Tabs>
      </DrawerContent>
      <DrawerFooter>
        <Button onClick={handleClose}>Close</Button>
        <Button onClick={handleSave} disabled={isSaveDisabled} variant={ButtonVariants.Primary}>
          Save
        </Button>
      </DrawerFooter>
    </Flex>
  );
}
